/**
 * Central auth token store for access + refresh tokens.
 * Access token lives in memory; refresh in localStorage (web).
 * Designed so mobile can swap storage (e.g. Secure Storage) later.
 */

const REFRESH_TOKEN_KEY = "DoitifyRefreshToken";
const TOKEN_COOKIE_NAME = "Token";

let accessToken = null;

function setCookie(name, value, maxAgeSeconds = 0) {
  if (typeof document === "undefined") return;
  const maxAge = maxAgeSeconds ? `; max-age=${maxAgeSeconds}` : "";
  document.cookie = `${name}=${encodeURIComponent(value || "")}${maxAge}; path=/; SameSite=Lax`;
}

function clearCookie(name) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; path=/; max-age=0`;
}

function getCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(
      "(?:^|;\\s*)" +
        name.replace(/[\\.*+?^${}()|[\]\\\\]/g, "\\$&") +
        "=([^;]*)",
    ),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

export function getAccessToken() {
  if (accessToken) return accessToken;
  const fromCookie = getCookie(TOKEN_COOKIE_NAME);
  if (fromCookie) {
    accessToken = fromCookie;
    return fromCookie;
  }
  return null;
}

export function getRefreshToken() {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY) || null;
}

export function setTokens(newAccessToken, newRefreshToken) {
  accessToken = newAccessToken || null;
  if (typeof localStorage !== "undefined" && newRefreshToken != null) {
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
  }
  if (typeof document !== "undefined" && newAccessToken != null) {
    setCookie(TOKEN_COOKIE_NAME, newAccessToken, 15 * 60);
  }
}

export function clearTokens() {
  accessToken = null;
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }
  if (typeof document !== "undefined") {
    clearCookie(TOKEN_COOKIE_NAME);
  }
}

export function isAuthenticated() {
  return Boolean(getAccessToken() || getRefreshToken());
}

/**
 * Cheap client-side expiry check on the JWT access token (no signature
 * verification — the server remains the authority). Returns true when the
 * token is missing or its `exp` is within `skewMs` of now. Undecodable tokens
 * are treated as fresh so the server can decide.
 */
export function isAccessTokenExpired(skewMs = 15000) {
  const token = getAccessToken();
  if (!token) return true;
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    if (typeof payload?.exp !== "number") return false;
    return payload.exp * 1000 <= Date.now() + skewMs;
  } catch {
    return false;
  }
}

/**
 * Extract the user id (`_id` claim) from the access token payload, or null.
 * Lets session helpers resync localStorage("UserID") after a token refresh.
 */
export function getUserIdFromAccessToken() {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const base64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    return payload?._id || null;
  } catch {
    return null;
  }
}
